// src/components/panoramica/PanoramicaDashboard.js
// "Panoramica squadra" (rifatta): regole di lega, undici migliore per la
// prossima giornata con previsione per giocatore, scelte titolare/panchina
// spiegate, bollettino infortunati e prossima partita di ognuno.
// Previsioni: forecastEngine.js · scelta formazione: lineupOptimizer.js.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, RotateCcw, Eye, Radar, ShieldCheck, Bandage, CalendarDays, Scale, SlidersHorizontal, Info, ChevronDown,
} from 'lucide-react';
import TeamBadge from '../TeamBadge';
import {
  getAuctionPlayerCategories, saveAuctionPlayerCategory, updateAuction,
} from '../../firebase/auctionService';
import { getAllPlayerRatingsMap } from '../../firebase/playerService';
import { ensureInjuriesSynced } from '../../firebase/injuriesService';
import { buildPlayerAvailability } from '../../utils/buildPlayerAvailability';
import { buildRosterOutlook, getPurchaseName, getPurchaseTeam } from '../auction/panoramicaEngine';
import { getUpcomingTeamFixtures, formatFixtureDate } from '../../data/serieACalendar';
import { buildLeagueContext, forecastPlayer, normalizeRules } from './forecastEngine';
import { optimizeLineup } from './lineupOptimizer';
import RulesPanel from './RulesPanel';
import ForecastPitch from './ForecastPitch';
import WhyDuels from './WhyDuels';
import InjuryBulletin from './InjuryBulletin';
import NextMatchList from './NextMatchList';
import PlayerSheet from './PlayerSheet';
import './panoramica.css';

const pid = (p) => String(p?.playerId ?? p?.id ?? '');

// La categoria "Infortunio" impostata a mano (qui o in Market/Svincolati) vale
// come un infortunio vero: è un giudizio dell'utente, più affidabile della
// fonte automatica quando questa è in ritardo.
function mergeManualInjuries(availabilityById, roster, categoriesById) {
  const merged = new Map(availabilityById || []);
  (roster || []).forEach((p) => {
    const id = pid(p);
    if (!id || categoriesById[id] !== 'injury') return;
    const existing = merged.get(id) || {};
    if (!existing.injury) merged.set(id, { ...existing, injury: { reason: 'Segnalato come indisponibile' } });
  });
  return merged;
}

function ModuleCompare({ modules, selected, bestKey, onSelect }) {
  const complete = modules.filter((m) => m.complete);
  if (!complete.length) return null;
  const max = Math.max(...complete.map((m) => m.total));
  const min = Math.min(...complete.map((m) => m.total));
  const span = Math.max(1, max - min);
  return (
    <div className="pz-card pz-modules">
      <div className="pz-side__head">
        <strong>Confronto moduli</strong>
        <em>Punti attesi per modulo · tocca per vederlo in campo</em>
      </div>
      <ul>
        {complete.map((m) => {
          const w = 18 + ((m.total - min) / span) * 82;
          return (
            <li key={m.key}>
              <button type="button" className={`pz-module ${m.key === selected ? 'is-active' : ''}`} onClick={() => onSelect(m.key)}>
                <span className="pz-module__key">{m.key}</span>
                <span className="pz-module__bar"><i style={{ width: `${w}%` }} /></span>
                <span className="pz-module__val">{m.total.toFixed(1)}</span>
                <span className="pz-module__tags">
                  {m.key === bestKey && <em className="is-best">consigliato</em>}
                  {m.defMod > 0.05 && <em>+{m.defMod.toFixed(1)} mod.</em>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// loadRatings: fonte della tabella voti (di default Firestore); sostituibile
// per verificare la pagina con dati reali senza login.
export default function PanoramicaDashboard({ auction, ownerUid, rolesMode, myTeamName, myRosterPurchases, players, onChangeAuction, isWatched, loadRatings = getAllPlayerRatingsMap }) {
  const [ratingsById, setRatingsById] = useState(null);
  const [rawAvailabilityById, setRawAvailabilityById] = useState(null);
  const [categoriesById, setCategoriesById] = useState({});
  const [dataReady, setDataReady] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rawRules, setRawRules] = useState(() => auction?.scoringRules || {});
  const [selectedModule, setSelectedModule] = useState(null);
  const [sheetItem, setSheetItem] = useState(null);
  const [now] = useState(() => Date.now());

  const rules = useMemo(() => normalizeRules(rawRules), [rawRules]);

  // Regole salvate sull'asta (come prima), con un piccolo ritardo mentre si
  // scrivono i numeri.
  const saveTimer = useRef(null);
  const handleRulesChange = useCallback((next) => {
    setRawRules(next);
    setSelectedModule(null);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      updateAuction(ownerUid, auction?.id, { scoringRules: next }).catch(() => {});
    }, 600);
  }, [ownerUid, auction?.id]);
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  useEffect(() => {
    let cancelled = false;
    setDataReady(false);
    ensureInjuriesSynced().catch(() => {});
    Promise.all([
      loadRatings().catch(() => null),
      buildPlayerAvailability(players || []).catch(() => null),
      getAuctionPlayerCategories(ownerUid, auction?.id).catch(() => ({})),
    ]).then(([ratings, availability, categories]) => {
      if (cancelled) return;
      setRatingsById(ratings);
      setRawAvailabilityById(availability);
      setCategoriesById(categories || {});
      setDataReady(true);
    });
    return () => { cancelled = true; };
  }, [players, ownerUid, auction?.id, loadRatings]);

  const availabilityById = useMemo(
    () => mergeManualInjuries(rawAvailabilityById, myRosterPurchases, categoriesById),
    [rawAvailabilityById, myRosterPurchases, categoriesById]
  );
  const manualIds = useMemo(
    () => new Set(Object.entries(categoriesById).filter(([, c]) => c === 'injury').map(([id]) => id)),
    [categoriesById]
  );

  // Modello di campionato: forza delle squadre ed effetti, dalla tabella voti.
  const ctx = useMemo(() => (dataReady ? buildLeagueContext({ ratingsById: ratingsById || new Map(), now }) : null), [dataReady, ratingsById, now]);

  const forecasts = useMemo(() => {
    if (!ctx) return [];
    return (myRosterPurchases || []).map((purchase) => {
      const id = pid(purchase);
      const team = getPurchaseTeam(purchase);
      const role = purchase?.playerSnapshot?.role || purchase?.role || 'A';
      const fixture = getUpcomingTeamFixtures(team, 1, now)[0] || null;
      const forecast = forecastPlayer({
        playerId: id, role, team, fixture, ctx,
        availability: availabilityById.get(id) || null,
        manualOut: manualIds.has(id),
        rules,
      });
      return { purchase, forecast };
    });
  }, [ctx, myRosterPurchases, availabilityById, manualIds, rules, now]);

  // Chi è sicuramente fuori (infortunato o segnalato) non entra mai nell'undici.
  const selectable = useMemo(() => forecasts.filter((x) => x.forecast.pPlay > 0), [forecasts]);
  const opt = useMemo(() => optimizeLineup({ items: selectable, rolesMode, rules }), [selectable, rolesMode, rules]);
  const lineup = (selectedModule && opt.byKey[selectedModule]) || opt.best;
  const starterIds = useMemo(() => new Set((lineup?.starters || []).map((s) => s.forecast.playerId)), [lineup]);

  const injuries = useMemo(() => {
    if (!dataReady) return [];
    const rank = (e) => (e.injury.returnInPast ? 0 : (e.injury.expectedReturn ? 1 + e.injury.missedCount : 100));
    return buildRosterOutlook({ rosterPurchases: myRosterPurchases, ratingsById, availabilityById, count: 5 })
      .filter((e) => e.injury)
      .sort((a, b) => rank(b) - rank(a));
  }, [dataReady, myRosterPurchases, ratingsById, availabilityById]);

  const nextRound = useMemo(() => {
    const fx = forecasts.map((x) => x.forecast.fixture).filter(Boolean);
    if (!fx.length) return null;
    return fx.reduce((a, b) => (b.giornata < a.giornata ? b : a));
  }, [forecasts]);

  const markOut = useCallback((id) => {
    setBusyId(id);
    setCategoriesById((prev) => ({ ...prev, [id]: 'injury' }));
    setSelectedModule(null);
    saveAuctionPlayerCategory(ownerUid, auction?.id, id, 'injury').catch(() => {}).finally(() => setBusyId(null));
  }, [ownerUid, auction?.id]);
  const markIn = useCallback((id) => {
    setBusyId(id);
    setCategoriesById((prev) => { const n = { ...prev }; delete n[id]; return n; });
    setSelectedModule(null);
    saveAuctionPlayerCategory(ownerUid, auction?.id, id, 'none').catch(() => {}).finally(() => setBusyId(null));
  }, [ownerUid, auction?.id]);

  const sheetId = sheetItem?.forecast.playerId;
  // Il pannello mostra sempre la previsione aggiornata (es. dopo "non gioca").
  const sheetLive = sheetId ? forecasts.find((x) => x.forecast.playerId === sheetId) || sheetItem : null;

  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="pz-page">
      <header className="pz-header">
        <div>
          <span className="pz-eyebrow"><Radar size={13} /> Panoramica squadra</span>
          <h1>{auction.name}</h1>
          <span className="pz-header__sub">
            {myTeamName}
            {isWatched && <em><Eye size={12} /> Asta seguita</em>}
          </span>
        </div>
        <button type="button" className="pz-btn" onClick={onChangeAuction}><RotateCcw size={14} /> Cambia asta</button>
      </header>

      {!dataReady && <div className="pz-loading"><Loader2 size={20} className="spin" /> Analizzo voti e calendario…</div>}
      {dataReady && !myRosterPurchases.length && <div className="pz-empty">Non hai ancora giocatori in rosa in questa asta.</div>}

      {dataReady && myRosterPurchases.length > 0 && lineup && (
        <>
          <section className="pz-hero">
            <div className="pz-hero__main">
              <span>{nextRound ? `Giornata ${nextRound.giornata} · ${formatFixtureDate(nextRound.date)}` : 'Prossima giornata'}</span>
              <b>{lineup.total.toFixed(1)}</b>
              <em>punti attesi · forchetta {lineup.low.toFixed(0)}–{lineup.high.toFixed(0)}</em>
            </div>
            <div className="pz-hero__kpis">
              <div><span>Modulo</span><b>{lineup.formationKey}</b></div>
              {lineup.captain && (
                <button type="button" onClick={() => setSheetItem(lineup.captain)}>
                  <span>Capitano</span><b>{getPurchaseName(lineup.captain.purchase)}</b>
                </button>
              )}
              <div><span>Mod. difesa</span><b>{rules.defenseModifier ? (lineup.defenseModifier.applies ? `+${lineup.defenseModifier.expected.toFixed(1)}` : 'no') : 'off'}</b></div>
              <button type="button" onClick={() => goTo('pz-infortuni')} className={injuries.length ? 'is-bad' : ''}>
                <span>Infortunati</span><b>{injuries.length}</b>
              </button>
            </div>
          </section>

          <nav className="pz-nav" aria-label="Sezioni">
            <button type="button" onClick={() => goTo('pz-formazione')}><ShieldCheck size={13} /> Formazione</button>
            <button type="button" onClick={() => goTo('pz-perche')}><Scale size={13} /> Perché</button>
            <button type="button" onClick={() => goTo('pz-infortuni')}><Bandage size={13} /> Infortuni</button>
            <button type="button" onClick={() => goTo('pz-prossima')}><CalendarDays size={13} /> Prossima partita</button>
            <button type="button" onClick={() => goTo('pz-regole')}><SlidersHorizontal size={13} /> Regole</button>
          </nav>

          <RulesPanel rules={{ ...rules, ...rawRules, defModBands: rawRules.defModBands || rules.defModBands }} onChange={handleRulesChange} />

          <section className="pz-section" id="pz-formazione">
            <div className="pz-section__head">
              <h2><ShieldCheck size={16} /> Formazione consigliata</h2>
              <span>
                Gli 11 che rendono di più <b>in media</b> nella prossima giornata, contando chi rischia di non giocare (e
                chi entrerebbe al suo posto), il modificatore di difesa e il capitano.
                {selectedModule && selectedModule !== opt.best?.formationKey && (
                  <> Stai guardando il <b>{selectedModule}</b>. <button type="button" className="pz-link" onClick={() => setSelectedModule(null)}>Torna al consigliato</button></>
                )}
              </span>
            </div>
            <div className="pz-xi">
              <div className="pz-xi__pitch">
                <ForecastPitch lineup={lineup} onSelect={setSheetItem} />
                <div className="pz-legend">
                  <span><i className="pz-legend__ring" /> anello = probabilità di giocare</span>
                  <span><i className="pz-legend__fv" /> fantavoto atteso</span>
                  {rules.defenseModifier && !lineup.defenseModifier.applies && (
                    <span className="is-warn">Mod. difesa non attivo: {lineup.defenseModifier.reason}</span>
                  )}
                </div>
              </div>
              <div className="pz-xi__side">
                <ModuleCompare
                  modules={opt.modules}
                  selected={lineup.formationKey}
                  bestKey={opt.best?.formationKey}
                  onSelect={(k) => setSelectedModule(k === opt.best?.formationKey ? null : k)}
                />
                <div className="pz-card pz-bench">
                  <div className="pz-side__head"><strong>Panchina</strong><em>Nell'ordine in cui conviene schierarla</em></div>
                  <ul>
                    {lineup.bench.map((b) => (
                      <li key={b.forecast.playerId}>
                        <button type="button" onClick={() => setSheetItem(b)}>
                          <TeamBadge team={getPurchaseTeam(b.purchase)} size={20} showTooltip={false} />
                          <span className="pz-bench__name">{getPurchaseName(b.purchase)}</span>
                          <em>{b.forecast.role}</em>
                          <b>{b.forecast.expFV.toFixed(1)}</b>
                          <span className="pz-bench__p">{Math.round(b.forecast.pPlay * 100)}%</span>
                        </button>
                      </li>
                    ))}
                    {!lineup.bench.length && <li className="pz-empty">Nessuna riserva disponibile.</li>}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="pz-section" id="pz-perche">
            <div className="pz-section__head">
              <h2><Scale size={16} /> Perché lui e non lui</h2>
              <span>Ogni titolare contro la migliore alternativa in panchina nello stesso ruolo. In cima le scelte più in bilico.</span>
            </div>
            <WhyDuels lineup={lineup} onSelect={setSheetItem} />
          </section>

          <section className="pz-section" id="pz-infortuni">
            <div className="pz-section__head">
              <h2><Bandage size={16} /> Bollettino infortunati</h2>
              <span>Chi è fermo, quando dovrebbe tornare schierabile e quali partite salterà (barrate).</span>
            </div>
            <InjuryBulletin entries={injuries} rolesMode={rolesMode} manualIds={manualIds} onMarkIn={markIn} />
          </section>

          <section className="pz-section" id="pz-prossima">
            <div className="pz-section__head">
              <h2><CalendarDays size={16} /> Prossima partita</h2>
              <span>Per ogni giocatore: avversario, fantavoto atteso con forchetta, probabilità di giocare, di segnare o di porta inviolata, e ultimi fantavoti.</span>
            </div>
            <NextMatchList items={forecasts} rolesMode={rolesMode} starterIds={starterIds} onSelect={setSheetItem} />
          </section>

          <details className="pz-card pz-method">
            <summary><Info size={14} /> Come funziona la previsione <ChevronDown size={14} /></summary>
            <ul>
              <li><b>Forza delle squadre:</b> ricostruisco i risultati di ogni partita giocata dalla tabella voti e stimo attacco e difesa di ogni squadra (modello di Poisson con fattore campo), pesando di più le ultime giornate. A inizio stagione, con poche partite, la stima parte dai valori pre-campionato.</li>
              <li><b>Voto:</b> media dei voti del giocatore con più peso alle ultime partite, corretta per l'avversario e per il campo (effetti stimati su tutto il campionato, per ruolo).</li>
              <li><b>Bonus:</b> la quota di gol e assist del giocatore sulla sua squadra × i gol che la squadra dovrebbe segnare contro quell'avversario. Per il portiere: gol subiti attesi e probabilità di porta inviolata.</li>
              <li><b>Chi gioca:</b> presenze reali, probabili formazioni e infortuni. Chi rischia viene pesato insieme alla riserva che entrerebbe al suo posto.</li>
              <li><b>Affidabilità:</b> verificata sulle giornate già giocate (G3–G7): errore medio sul fantavoto inferiore di circa l'11% rispetto alla semplice media stagionale. Resta una stima: una singola giornata oscilla molto (la forchetta lo dice).</li>
            </ul>
          </details>
        </>
      )}

      <PlayerSheet
        item={sheetLive}
        rolesMode={rolesMode}
        isStarter={sheetLive ? starterIds.has(sheetLive.forecast.playerId) : false}
        isCaptain={sheetLive ? lineup?.captain?.forecast.playerId === sheetLive.forecast.playerId : false}
        isVice={sheetLive ? lineup?.vice?.forecast.playerId === sheetLive.forecast.playerId : false}
        manualOut={sheetLive ? manualIds.has(sheetLive.forecast.playerId) : false}
        busy={busyId === sheetId}
        onClose={() => setSheetItem(null)}
        onMarkOut={() => markOut(sheetId)}
        onMarkIn={() => markIn(sheetId)}
      />
    </div>
  );
}
